import { ToastType } from '@components/Toast';
import { handleToast } from '@utils/utils';

import {
  addApprovalFlagCommentApi,
  checkLoadDetailsApi,
  deleteInvoicingDocumentApi,
  fetchOrderForAccountingInfo,
  generateEdiInvoiceApi,
  getCustomerInvoiceApi,
  processInvoicingOrderApi,
  reprocessDocumentsApi,
  sendEmailRequest,
  sendMailRequest,
  setCarrierBilledDetailsRequest,
  setCarrierUpdateAddressRequest,
  setFactoringCompanyRequest,
  setOrderInvoicedDateRequest,
  setVendorBillingInfoRequest,
  syncInvoiceApi,
  updateDropOffDatesRequest,
  voidCheckApi,
} from './services';
import { accountingOrder, setAccountingOrder } from './store';
import { ProcessInvoicingPayload } from './types';

export const fetchAccountingOrderDetails = async (
  orderId: number,
  cb?: () => void,
) => {
  const resp = await fetchOrderForAccountingInfo(orderId);
  if (resp) {
    setAccountingOrder('order', resp);
    setAccountingOrder('emailModalFormVals', {
      to: resp.customer.contacts
        .filter(
          (contact) => Boolean(contact.email) && contact.type === 'Billing',
        )
        .map((contact) => contact.email || ''),
      from: 'ar@armstrongtransport.com',
      subject: `Armstrong Invoice # ${resp.id}`,
      attachmentName: `Armstrong Invoice # ${resp.id}.pdf`,
    });
  }
  cb && cb();
};

export const makeSendEmailRequest = async (
  orderId: number,
  cb?: () => void,
) => {
  const resp = await sendEmailRequest(orderId, {
    ...accountingOrder.emailModalFormVals,
  });
  if (resp && resp.success) {
    handleToast(ToastType.Success, 'Email sent successfully');
  }
  cb && cb();
};

export const makeSendMailRequest = async (
  orderId: number,
  invoiceUrl: string,
  cb?: () => void,
) => {
  const resp = await sendMailRequest(orderId, invoiceUrl);
  if (resp && resp.isSuccess) {
    handleToast(ToastType.Success, 'Mail sent successfully');
  }
  cb && cb();
};

export const updateDropOffDatesAction = async (
  stopDropUpdates: unknown,
  successCallback: () => void,
) => {
  const resp = await updateDropOffDatesRequest(stopDropUpdates);
  if (resp && resp.isSuccess) {
    successCallback();
    handleToast(ToastType.Success, 'Drop off date updated successfully');
  }
};

export const updateOrderInvoicedDateRequest = async (
  orderId: number,
  date: string,
) => {
  const response = await setOrderInvoicedDateRequest(orderId, date);
  if (response && response.isSuccess) {
    handleToast(ToastType.Success, 'Order Invoice Date set successfully');
  }
};

export const updateVendorDetails = async (payload: Record<string, unknown>) => {
  const resp = await setVendorBillingInfoRequest(payload);
  if (Boolean(resp?.isSuccess)) {
    handleToast(ToastType.Success, 'Vendor Contract Info Saved');
  }
};

export const updateCarrierAddress = async (
  payload: Record<string, unknown>,
) => {
  const resp = await setCarrierUpdateAddressRequest(payload);
  if (resp) {
    handleToast(ToastType.Success, 'Carrier Address Updated');
  }
};

export const updateCarrierFactoringCompanyFromInvoice = async (
  factorId: number,
  carrierId: number,
) => {
  const resp = await setFactoringCompanyRequest(factorId, carrierId);
  if (Boolean(resp?.isSuccess)) {
    handleToast(ToastType.Success, 'Factoring company assigned successfully');
  }
};

export const updateCarrierBilledDetailsRequest = async (payload: {
  billRef: string | null;
  loadId: number;
  quickPay: boolean;
  billedDate: string | null;
  expectedPaidDate: string | null;
}) => {
  const response = await setCarrierBilledDetailsRequest(payload);
  if (response && response.isSuccess) {
    handleToast(
      ToastType.Success,
      'Carrier billed details updated successfully',
    );
  }
};

export const voidCheck = async (paymentId: number, txnId: string) => {
  const resp = await voidCheckApi(paymentId, txnId);
  return resp;
};

export const checkLoadDetails = async (paymentRef: string) => {
  const resp = await checkLoadDetailsApi(paymentRef);
  return resp;
};

export const processInvoicingOrder = async (
  processingOrderPayload: ProcessInvoicingPayload,
) => {
  const resp = await processInvoicingOrderApi(processingOrderPayload);
  return resp;
};

export const generateEdiInvoice = async (
  orderId: number,
  documents: string[],
) => {
  const resp = await generateEdiInvoiceApi(orderId, documents);
  if (resp && resp.success) {
    handleToast(ToastType.Success, 'EDI Invoice generated successfully');
  }
};

export const syncInvoice = async (invoiceId: string) => {
  const resp = await syncInvoiceApi(invoiceId);
  return resp;
};

export const reprocessInvoiceDocuments = async (
  loadNumber: string,
  quickPay: boolean,
) => {
  const resp = await reprocessDocumentsApi(loadNumber, quickPay);
  return resp;
};

export const deleteInvoicingDocument = async (fileName: string) => {
  const resp = await deleteInvoicingDocumentApi(fileName);
  if (resp && resp.isSuccess) {
    handleToast(ToastType.Success, 'Document deleted successfully');
  }
};

export const addApprovalFlagComment = async (
  orderId: number,
  comment: string,
  flagId?: number,
) => {
  const resp = await addApprovalFlagCommentApi(orderId, comment, flagId);
  return resp;
};

export const generateCustomerInvoice = async (orderId: number) => {
  const urls = accountingOrder.documentUrls;
  const resp = await getCustomerInvoiceApi(orderId, urls);
  return resp;
};
